<template>
  <div class="pin-keypad" role="group">
    <button
      v-for="d in [1, 2, 3, 4, 5, 6, 7, 8, 9]"
      :key="d"
      type="button"
      class="pin-keypad__key pin-keypad__key--digit"
      :class="{ 'pin-keypad__key--pressed': pressed === String(d) }"
      :disabled="disabled"
      @pointerdown="onPress($event, () => $emit('digit', String(d)))"
      @click="onKeyboardClick($event, () => $emit('digit', String(d)))"
    >{{ d }}</button>

    <!-- bottom-left: Face ID (biometric) when available on this device, else an empty slot -->
    <button
      v-if="bio"
      type="button"
      class="pin-keypad__key pin-keypad__key--aux"
      :aria-label="t('login.pin.biometric')"
      :disabled="disabled"
      @pointerdown="onPress($event, () => $emit('biometric'))"
      @click="onKeyboardClick($event, () => $emit('biometric'))"
    >
      <IconFaceId :size="26" />
    </button>
    <span v-else />

    <button
      type="button"
      class="pin-keypad__key pin-keypad__key--digit"
      :class="{ 'pin-keypad__key--pressed': pressed === '0' }"
      :disabled="disabled"
      @pointerdown="onPress($event, () => $emit('digit', '0'))"
      @click="onKeyboardClick($event, () => $emit('digit', '0'))"
    >0</button>

    <!-- bottom-right: backspace (always) -->
    <button
      type="button"
      class="pin-keypad__key pin-keypad__key--aux"
      :class="{ 'pin-keypad__key--pressed': pressed === 'backspace' }"
      :aria-label="t('common.delete')"
      :disabled="disabled"
      @pointerdown="onPress($event, () => $emit('backspace'))"
      @click="onKeyboardClick($event, () => $emit('backspace'))"
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4"
           stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M9 4h11a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H9L2 12z" /><path d="m12 9 6 6M18 9l-6 6" />
      </svg>
    </button>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import IconFaceId from '@/components/ui/icons/IconFaceId.vue';
import { activeKeypads, pinKeyFromEvent } from './pinKeyboard';

const props = withDefaults(defineProps<{
  bio?: boolean;
  disabled?: boolean;
}>(), { bio: false, disabled: false });

const emit = defineEmits<{ digit: [d: string]; backspace: []; biometric: [] }>();

const { t } = useI18n();

/**
 * Typing the PIN on a computer keyboard (DVL-030): digits (top row and numpad) and Backspace/Delete, while this
 * keypad is on screen. Lives here so every PIN screen gets it at once (app lock, PIN login, PIN setup, step-up,
 * card PIN).
 *
 * Only the most recently mounted keypad listens: a lock screen over a screen that has its own keypad must not
 * receive each digit twice. Keys typed into a text field (an e-mail code next to the keypad) are left alone.
 */
const instance = Symbol('pin-keypad');
const pressed = ref<string | null>(null);
let pressedTimer: ReturnType<typeof setTimeout> | null = null;

function onKeydown(e: KeyboardEvent) {
  if (props.disabled || activeKeypads[activeKeypads.length - 1] !== instance) return;
  const key = pinKeyFromEvent(e);
  if (!key) return;
  e.preventDefault();
  if (key === 'backspace') emit('backspace');
  else emit('digit', key);
  // Light up the key that was typed, as a tap would.
  pressed.value = key;
  if (pressedTimer) clearTimeout(pressedTimer);
  pressedTimer = setTimeout(() => { pressed.value = null; }, 120);
}

onMounted(() => {
  activeKeypads.push(instance);
  window.addEventListener('keydown', onKeydown);
});
onUnmounted(() => {
  window.removeEventListener('keydown', onKeydown);
  const i = activeKeypads.indexOf(instance);
  if (i >= 0) activeKeypads.splice(i, 1);
  if (pressedTimer) clearTimeout(pressedTimer);
});

/**
 * A key fires on the touch itself, not on `click` (iOS 27, 02.10). iOS turned a quick second tap on the same digit
 * into a double-tap gesture and swallowed it, and a tap whose finger moved a little became a scroll of the
 * (slightly taller than the screen) PIN view — no click at all. Only the primary button of a mouse counts.
 */
function onPress(e: PointerEvent, fire: () => void) {
  if (e.button !== 0) return;
  e.preventDefault();   // no focus ring, no synthetic mouse events, no gesture start
  if (!props.disabled) fire();   // some engines deliver pointer events to a disabled button
}

/** A hardware keyboard (Enter/Space) still presses a key: its click has no pointer behind it (detail 0). */
function onKeyboardClick(e: MouseEvent, fire: () => void) {
  if (e.detail === 0 && !props.disabled) fire();
}
</script>

<style scoped lang="scss">
.pin-keypad {
  display: grid;
  grid-template-columns: repeat(3, 68px);
  gap: 10px 34px;
  justify-content: center;
  // A touch on the keypad is always a key press — never a scroll, a zoom or a double-tap gesture.
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
}
.pin-keypad__key {
  width: 68px;
  height: 68px;
  border-radius: 50%;
  background: transparent;
  border: none;
  color: var(--text);
  font-family: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.12s;
  -webkit-tap-highlight-color: transparent;

  &:active, &.pin-keypad__key--pressed { background: var(--soft); }
  &:disabled { color: var(--text-3); cursor: default; }
}
.pin-keypad__key--digit { font-size: 28px; font-weight: 400; }
.pin-keypad__key--aux {
  color: var(--gold);
  svg { width: 26px; height: 26px; }
}
</style>
